// Client Portal — RAG API Keys panel. Extracted from App.js.
import { useState } from "react";
import {
  AlertCircle,
  BookOpen,
  Check,
  Copy,
  Key,
  PlusCircle,
  ShieldCheck,
  Terminal,
  Trash2,
} from "lucide-react";
import {
  generateApiKey,
  loadApiKeys,
  logAudit,
  saveApiKeys,
} from "../lib/ragConfig";

export const RAGApiKeys = ({ facility, user }) => {
  const fid = facility.facility_id;
  const [keys, setKeys] = useState(() => loadApiKeys(fid));
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyQuota, setNewKeyQuota] = useState(1000);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const createKey = () => {
    if (!newKeyName.trim()) return;
    const key = {
      id: `key-${Date.now()}`,
      name: newKeyName.trim(),
      key: generateApiKey(),
      quota: newKeyQuota,
      used: 0,
      createdAt: new Date().toISOString(),
      createdBy: user.email,
      active: true,
    };
    const updated = [key, ...keys];
    setKeys(updated);
    saveApiKeys(fid, updated);
    logAudit(fid, "api_key_created", user.email, { keyName: key.name });
    setNewKeyName("");
    setNewKeyQuota(1000);
    setShowCreate(false);
  };

  const revokeKey = (keyId) => {
    const updated = keys.map((k) =>
      k.id === keyId
        ? { ...k, active: false, revokedAt: new Date().toISOString() }
        : k,
    );
    setKeys(updated);
    saveApiKeys(fid, updated);
    const k = keys.find((x) => x.id === keyId);
    logAudit(fid, "api_key_revoked", user.email, { keyName: k?.name });
  };

  const deleteKey = (keyId) => {
    const k = keys.find((x) => x.id === keyId);
    const updated = keys.filter((x) => x.id !== keyId);
    setKeys(updated);
    saveApiKeys(fid, updated);
    logAudit(fid, "api_key_deleted", user.email, { keyName: k?.name });
  };

  const copyKey = (keyId, keyValue) => {
    navigator.clipboard.writeText(keyValue);
    setCopiedId(keyId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const activeKeys = keys.filter((k) => k.active);
  const revokedKeys = keys.filter((k) => !k.active);

  const apiBase = window.location.origin + "/auth";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">API Keys</h3>
          <p className="text-sm text-slate-500">
            Programmatic access to {facility.facility_name} RAG
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowDocs(!showDocs)}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showDocs
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            API Docs
          </button>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Create Key
          </button>
        </div>
      </div>

      {/* API Docs panel */}
      {showDocs && (
        <div className="bg-slate-900 rounded-xl p-6 text-sm">
          <h4 className="text-white font-semibold mb-3 flex items-center">
            <Terminal className="w-4 h-4 mr-2" />
            API Reference
          </h4>
          <div className="space-y-4">
            <div>
              <p className="text-slate-400 mb-2">Query the knowledge base:</p>
              <pre className="bg-slate-800 rounded-lg p-4 text-green-400 overflow-x-auto">
                {`curl -X POST ${apiBase}/rag/chat \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "facility_id": "${fid}",
    "chatInput": "What are the key policies?",
    "sessionId": "optional-session-id"
  }'`}
              </pre>
            </div>
            <div>
              <p className="text-slate-400 mb-2">Response format:</p>
              <pre className="bg-slate-800 rounded-lg p-4 text-blue-400 overflow-x-auto">
                {`{
  "output": "Based on the documents...",
  "sourceDocuments": [
    {
      "pageContent": "...",
      "metadata": {
        "source": "policy.pdf",
        "page": 3,
        "score": 0.92
      }
    }
  ]
}`}
              </pre>
            </div>
            <div>
              <p className="text-slate-400 mb-2">Upload a document:</p>
              <pre className="bg-slate-800 rounded-lg p-4 text-green-400 overflow-x-auto">
                {`curl -X POST ${apiBase}/rag/documents/upload \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "file=@document.pdf" \\
  -F "facility_id=${fid}"`}
              </pre>
            </div>
            <div className="border-t border-slate-700 pt-3">
              <p className="text-slate-500 text-xs">
                Rate limits apply based on key quota. Include the API key in the
                Authorization header as a Bearer token.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create key form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900 mb-4">
            Create New API Key
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Key Name
              </label>
              <input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production Backend, Mobile App"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Daily Quota
              </label>
              <input
                type="number"
                value={newKeyQuota}
                onChange={(e) =>
                  setNewKeyQuota(
                    Math.max(
                      1,
                      Math.min(100000, parseInt(e.target.value) || 0),
                    ),
                  )
                }
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={createKey}
              disabled={!newKeyName.trim()}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 transition-colors"
            >
              Generate Key
            </button>
          </div>
        </div>
      )}

      {/* Active keys */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200">
          <h4 className="text-sm font-semibold text-slate-900">
            Active Keys ({activeKeys.length})
          </h4>
        </div>
        {activeKeys.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <Key className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              No active API keys. Create one to get started.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {activeKeys.map((k) => (
              <div
                key={k.id}
                className="px-6 py-4 flex items-center justify-between"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-slate-900">
                      {k.name}
                    </span>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                      Active
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <code className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded font-mono">
                      {k.key.slice(0, 12)}...{k.key.slice(-6)}
                    </code>
                    <span className="text-xs text-slate-400">
                      Created {new Date(k.createdAt).toLocaleDateString()}
                    </span>
                    <span className="text-xs text-slate-400">
                      Quota: {k.quota.toLocaleString()}/day
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyKey(k.id, k.key)}
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Copy full key"
                  >
                    {copiedId === k.id ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => revokeKey(k.id)}
                    className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                    title="Revoke key"
                  >
                    <ShieldCheck className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revoked keys */}
      {revokedKeys.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h4 className="text-sm font-semibold text-slate-500">
              Revoked Keys ({revokedKeys.length})
            </h4>
          </div>
          <div className="divide-y divide-slate-100">
            {revokedKeys.map((k) => (
              <div
                key={k.id}
                className="px-6 py-3 flex items-center justify-between opacity-60"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600 line-through">
                      {k.name}
                    </span>
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full font-medium">
                      Revoked
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Revoked{" "}
                    {k.revokedAt
                      ? new Date(k.revokedAt).toLocaleDateString()
                      : "—"}
                  </p>
                </div>
                <button
                  onClick={() => deleteKey(k.id)}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete permanently"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-900">Security Note</p>
            <p className="text-xs text-amber-700 mt-1">
              API keys grant full access to this facility&apos;s RAG service.
              Store keys securely — they are shown only once at creation. Revoke
              keys immediately if compromised. For production use, connect a
              backend key validation endpoint.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Audit Log ─────────────────────────────────────────────────────
